import { Alert, Button, Result } from 'antd';
import { useTranslation } from 'react-i18next';

/**
 * `state: "unknown"` — the core could not establish the account state (its
 * credential/account read failed, or none is installed).
 *
 * This screen exists so that path never has to LIE. The core used to synthesise a
 * state from the session token's single `blocked` boolean, which meant an
 * administratively blocked or switched-off subscriber was told "your access is
 * opening" (it never was), and a never-activated one was sent to the block screen
 * where no activate button exists. `unknown` says only what is true — billing cannot
 * answer right now — and offers the one action that can change that: re-poll.
 *
 * The device registration that preceded this screen DID succeed, and saying so
 * matters: otherwise a subscriber reads "unavailable" as "registration failed" and
 * loops.
 *
 * ⚠ «DID SUCCEED» IS NO LONGER UNCONDITIONAL (D408, 2026-09-01). The core now answers
 * `200 outcome:"no_device_identity"` for an ISP address with no captive lease behind
 * it, so this screen is reachable with nothing bound — and the body's opening clause,
 * «Устройство зарегистрировано, но …», would then be a claim nobody verified stated
 * on the one screen whose entire purpose is not to assert things it cannot know. The
 * unconfirmed body drops that clause and says only what is true.
 */
export function StateUnavailableScreen({
  registrationConfirmed = true,
  refreshing,
  onRefresh,
  onRelogin,
}: {
  /** False when no binding was written or confirmed in this session (D408). */
  registrationConfirmed?: boolean;
  refreshing: boolean;
  onRefresh: () => void;
  onRelogin: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Result
      status="warning"
      title={t('unavailable.title')}
      subTitle={t(registrationConfirmed ? 'unavailable.body' : 'unavailable.bodyUnconfirmed')}
      style={{ padding: '8px 0' }}
      extra={[
        <Button type="link" key="relogin" onClick={onRelogin}>
          {t('unavailable.relogin')}
        </Button>,
      ]}
    >
      <Alert type="info" showIcon message={t('unavailable.note')} style={{ marginBottom: 16 }} />
      <Button block type="primary" loading={refreshing} onClick={onRefresh}>
        {t('unavailable.refresh')}
      </Button>
    </Result>
  );
}
